import type { ReactElement } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { IncludedContentMap, IncludedContentReference } from './handleADF.ts';
import {
	IncludedContentTocSubscribers,
	type IncludedContentEntry,
} from './IncludedContentTocSubscribers.tsx';

export type UseIncludedContentTocHeadingsResult = {
	includedContentMap: IncludedContentMap;
	subscribersElement: ReactElement;
};

/**
 * Collects per-reference ADF content into a single `Map` keyed by the
 * `IncludedContentReference.key`. The actual data fetching is delegated to
 * `<IncludedContentTocSubscribers>` (returned as `subscribersElement`), which
 * mounts one tiny render-nothing component per reference. Those subscribers
 * reuse the macros' own GraphQL hooks, so no additional network requests are
 * triggered for the TOC.
 */
export const useIncludedContentTocHeadings = (
	references: IncludedContentReference[],
): UseIncludedContentTocHeadingsResult => {
	// Dedup references by their stable cache key. Multiple include macros that
	// point at the same content get a single subscriber.
	// `refKeys` is a stable string derived from the reference keys, so the memo
	// below keeps a stable identity even when `references` is a fresh array on
	// each render with the same logical contents.
	const refKeys = useMemo(
		() =>
			references
				.map((r) => r.key)
				.sort()
				.join('|'),
		[references],
	);
	const uniqueReferences = useMemo(() => {
		const byKey = new Map<string, IncludedContentReference>();
		for (const reference of references) {
			byKey.set(reference.key, reference);
		}
		return Array.from(byKey.values());
		// `references` is intentionally read inside the memo but excluded from the
		// deps; `refKeys` captures the only relevant change in its contents.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refKeys]);

	const [includedContentMap, setIncludedContentMap] = useState<IncludedContentMap>(
		() => new Map<string, ADFEntity[]>(),
	);

	// Held as a ref so the onEntry callback identity is stable across renders.
	// Without this, every render would re-mount the subscribers and re-trigger
	// their effects.
	const mapRef = useRef(includedContentMap);
	mapRef.current = includedContentMap;

	const handleEntry = useCallback((entry: IncludedContentEntry) => {
		const existing = mapRef.current.get(entry.key);
		if (existing && areArraysShallowEqual(existing, entry.content)) {
			return;
		}
		setIncludedContentMap((current) => {
			const next = new Map(current);
			next.set(entry.key, entry.content);
			return next;
		});
	}, []);

	const subscribersElement = useMemo(
		() => <IncludedContentTocSubscribers references={uniqueReferences} onEntry={handleEntry} />,
		[uniqueReferences, handleEntry],
	);

	return { includedContentMap, subscribersElement };
};

const areArraysShallowEqual = <T,>(a: T[], b: T[]): boolean => {
	if (a === b) {
		return true;
	}
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
};
